import {ObjectId} from 'bson';
import {type BrandedType} from '@packages/utilities/type-safety';

export enum CodeModuleLoaderType {
  FEDERATION = 'FEDERATION',
  SITE = 'SITE',
  NULL = 'NULL',
}

export enum CodeModuleOwnerType {
  Site = 'Site', // For site code components
  VersionedResource = 'VersionedResource', // For code libraries
  SharedResourceProfile = 'SharedResourceProfile', // For ws libraries sharing site code components
}

export type FederationLoaderConfig = {
  tag: CodeModuleLoaderType.FEDERATION;
  val: {
    clientModuleUrl: string;
    moduleId: CodeModuleId;
    submoduleId: string;
    exportPath: string;
  };
};

export type SiteLoaderConfig = {
  tag: CodeModuleLoaderType.SITE;
  val: {
    componentId: string;
  };
};

export type NullLoaderConfig = {
  tag: CodeModuleLoaderType.NULL;
  val: {
    componentId: string;
  };
};

export type CodeModuleId = BrandedType<'CodeModuleId', string>;
export type CodeModuleLoaderConfig =
  | FederationLoaderConfig
  | SiteLoaderConfig
  | NullLoaderConfig;

/**
 * Defines non retriable errors that can occur during renderer deployment to cloudflare.
 */
export const CodeModuleRendererErrorCodes = {
  '10021': 'BundleValidationError', // https://developers.cloudflare.com/workers/observability/errors/#validation-errors-10021
} as const;

export type CodeModuleRendererErrorCode =
  keyof typeof CodeModuleRendererErrorCodes;

/**
 * Cap on the stored Cloudflare rejection message.
 *
 * `10021` is an envelope: the message carries the uncaught exception from
 * instantiating the bundle, plus a multi-frame stack. It is the only thing that
 * distinguishes a global-scope violation from a missing module, so it is worth
 * storing — but it is attacker-influenced in length and must not grow a document
 * without bound.
 */
export const CODE_MODULE_DEPLOY_ERROR_MESSAGE_MAX_LENGTH = 2000;

export type CodeModuleOwner = {
  id: ObjectId | string;
  type: CodeModuleOwnerType;
};

export type ClientCodeModule = {
  bundleUrl: string;
  loaderType: CodeModuleLoaderType;
};

export type ServerCodeModule = {
  bundleUrl?: string | null;
  rendererUrl?: string | null;
  rendererVersion?: string | null;
  errorCode?: CodeModuleRendererErrorCode | null;
  errorMessage?: string | null;
  erroredAt?: Date | null;
  /**
   * Worker template version that recorded the error. A rejection is only
   * permanent for the template that produced it — see `isPermanentError` in
   * resolveCodeModuleWorkers.
   */
  errorVersion?: string | null;
};

/**
 * Library-level Code Functions module. Nested so bundle integrity fields
 * (`sha256`, `size`, …) and worker deploy state can grow independently.
 */
export type FunctionsCodeModule = {
  bundle: {
    /**
     * Private S3 key of the Code Functions bundle. Unlike the client and server modules
     * this is a key and not a url, because the bundle is never publicly readable.
     */
    key: string;
  };
  worker?: {
    url?: string | null;
    version?: string | null;
    errorCode?: CodeModuleRendererErrorCode | null;
    errorMessage?: string | null;
    erroredAt?: Date | null;
    errorVersion?: string | null;
  } | null;
};

export type CodeModuleWorkerUrls = {
  components?: string;
  functions?: string;
};
